import { Avatar, Box, Button, Card, CardContent, Chip, Drawer, IconButton, Typography } from '@mui/material';
import { LuCheck as Check, LuX as X } from 'react-icons/lu';
import { BuyRequestRow } from './types';

type Props = {
  open: boolean;
  requests: BuyRequestRow[];
  canManageMembers: boolean;
  actionLoadingId?: string | null;
  onApprove: (requestId: string) => void;
  onReject: (requestId: string) => void;
  onClose: () => void;
};

export function BuyRequestsPanel({ open, requests, canManageMembers, actionLoadingId, onApprove, onReject, onClose }: Props) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 3 }}
      PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, zIndex: (theme) => theme.zIndex.modal + 3 } }}
      BackdropProps={{ sx: { zIndex: (theme) => theme.zIndex.modal + 2 } }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ borderBottom: '1px solid #e2e8f0', px: 3, py: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#020617' }}>
                Join Requests
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500, color: '#64748b' }}>
                Review pending community requests.
              </Typography>
            </Box>

            <IconButton
              onClick={onClose}
              aria-label="Close panel"
              sx={{ width: 40, height: 40, border: '1px solid #e2e8f0', borderRadius: 2, color: '#64748b', backgroundColor: '#fff', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)' }}
            >
              <X size={18} />
            </IconButton>
          </Box>

          <Chip
            label={`${requests.length} Pending`}
            sx={{
              mt: 2,
              borderRadius: 999,
              border: '1px solid #c4b5fd',
              backgroundColor: '#f5f3ff',
              color: '#6d28d9',
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase'
            }}
          />
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          {!canManageMembers && requests.length > 0 && (
            <Box sx={{ mb: 1.5 }}>
              <Chip
                label="Only admin can approve or reject requests"
                sx={{ borderRadius: 2, backgroundColor: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa', fontWeight: 700 }}
              />
            </Box>
          )}

          {requests.length === 0 ? (
            <Box sx={{ border: '1px dashed #e2e8f0', borderRadius: 3, backgroundColor: '#f8fafc', px: 2, py: 5, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: '#475569' }}>
                No pending join requests.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {requests.map((request) => (
                <Card
                  key={request.requestId}
                  variant="outlined"
                  sx={{
                    borderRadius: 3,
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
                    '&:hover': { borderColor: '#c4b5fd', boxShadow: '0 8px 18px rgba(148, 163, 184, 0.15)' }
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={request.memberAvatar} alt={request.memberName} sx={{ width: 48, height: 48, border: '2px solid #fff', boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)', outline: '1px solid #e2e8f0' }} />
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#020617', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {request.memberName}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8' }}>
                          {request.memberEmail}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                      <Chip label={`Status: ${request.status || 'pending'}`} sx={{ borderRadius: 2, backgroundColor: '#f8fafc', color: '#334155', fontWeight: 700 }} />
                      <Chip label={`Created: ${request.createdAt ? new Date(request.createdAt).toLocaleDateString() : 'NA'}`} sx={{ borderRadius: 2, backgroundColor: '#eff6ff', color: '#1d4ed8', fontWeight: 700 }} />
                    </Box>

                    {request.requestMessage && (
                      <Typography variant="body2" sx={{ mt: 1.5, color: '#475569', fontWeight: 500 }}>
                        {request.requestMessage}
                      </Typography>
                    )}

                    {canManageMembers && request.status === 'pending' && (
                      <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        <Button
                          variant="outlined"
                          onClick={() => onReject(request.requestId)}
                          disabled={actionLoadingId === request.requestId}
                          sx={{
                            height: 40,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderColor: '#fecaca',
                            backgroundColor: '#fff1f2',
                            color: '#be123c',
                            '&:hover': { backgroundColor: '#ffe4e6', borderColor: '#fda4af' }
                          }}
                        >
                          Reject
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => onApprove(request.requestId)}
                          disabled={actionLoadingId === request.requestId}
                          sx={{
                            height: 40,
                            borderRadius: 2,
                            textTransform: 'none',
                            fontWeight: 700,
                            borderColor: '#86efac',
                            backgroundColor: '#f0fdf4',
                            color: '#166534',
                            '&:hover': { backgroundColor: '#dcfce7', borderColor: '#4ade80' }
                          }}
                          startIcon={<Check size={16} />}
                        >
                          Approve
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
